import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Users } from "lucide-react";
import { api } from "../api";
import { Panel, PageHead, Loading, LoadError, EmptyState, fmtDate } from "../ui";

export const AdminPatients = () => {
    const [search, setSearch] = useState("");
    const [country, setCountry] = useState("");
    const [countries, setCountries] = useState([]);
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const t = setTimeout(() => {
            setLoading(true);
            const params = new URLSearchParams({ page: String(page), limit: "20" });
            if (search.trim()) params.set("search", search.trim());
            if (country) params.set("country", country);
            api.get(`/admin/patients?${params.toString()}`)
                .then((res) => {
                    setData(res.data);
                    setCountries(res.data.countries || []);
                    setError("");
                })
                .catch(() => setError("Could not load patients. Please refresh."))
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(t);
    }, [search, country, page]);

    const items = data?.items || [];

    return (
        <div data-testid="admin-patients-page">
            <PageHead title="Patient Registry" subtitle="One record per patient — linked to every case they open." />
            <div className="relative max-w-md" data-testid="patients-filter-bar">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64716D]" />
                <input
                    data-testid="patients-search-input"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                    placeholder="Search name, email or phone…"
                    className="w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] pl-9 pr-4 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors"
                />
            </div>
            <select
                data-testid="patients-filter-country"
                value={country}
                onChange={(e) => {
                    setCountry(e.target.value);
                    setPage(1);
                }}
                aria-label="Filter by country"
                className="mt-3 rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-xs text-[#F7F5EF] outline-none focus:border-[#10B981] transition-colors"
            >
                <option value="">All countries</option>
                {countries.map((c) => (
                    <option key={c} value={c}>{c}</option>
                ))}
            </select>

            {error ? (
                <div className="mt-6"><LoadError message={error} /></div>
            ) : loading && !data ? (
                <Loading />
            ) : (
                <Panel className="mt-6 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left" data-testid="patients-table">
                            <thead>
                                <tr className="border-b border-[#1A2E2A] bg-[#0E1A17]">
                                    {["Name", "Email", "Phone", "Country", "City", "Cases", "First seen", "Last activity", ""].map((h) => (
                                        <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64716D] whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id || p.email} data-testid={`patient-row-${p.email}`} className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors">
                                        <td className="px-4 py-3 text-sm text-[#F7F5EF] font-medium whitespace-nowrap">{p.name}</td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{p.email}</td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{p.phone || "—"}</td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{p.country || "—"}</td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{p.city || "—"}</td>
                                        <td className="px-4 py-3 text-sm text-emerald-300 font-semibold whitespace-nowrap" data-testid={`patient-cases-count-${p.email}`}>
                                            {p.cases_count}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(p.created_at)}</td>
                                        <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(p.last_activity)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <Link to={`/admin/patients/${p.id}`} data-testid={`patient-open-${p.id}`} className="text-xs font-semibold text-emerald-300 hover:text-emerald-200">
                                                Open →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {items.length === 0 && (
                        <EmptyState icon={Users} title="No patients found" hint="Patients appear here when they submit their first enquiry." />
                    )}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-[#1A2E2A]" data-testid="patients-pagination">
                        <p className="text-xs text-[#64716D]" data-testid="patients-total-count">
                            {data?.total ?? 0} patient{(data?.total ?? 0) === 1 ? "" : "s"} · page {data?.page ?? 1} of {data?.pages ?? 1}
                        </p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                data-testid="patients-pagination-prev"
                                disabled={(data?.page ?? 1) <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded-lg border border-[#1D322E] px-3 py-1.5 text-xs text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                data-testid="patients-pagination-next"
                                disabled={(data?.page ?? 1) >= (data?.pages ?? 1)}
                                onClick={() => setPage((p) => p + 1)}
                                className="rounded-lg border border-[#1D322E] px-3 py-1.5 text-xs text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </Panel>
            )}
        </div>
    );
};
